import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getLiveDrivers, getNeedsAttentionRides } from "api/dispatchApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /dispatch — full-screen live dispatch view.
 *
 * Left rail = searchable + filterable driver list.
 * Center   = maplibregl map with status-coded markers.
 * Auto-refreshes every 15s when "live" toggle is on.
 *
 * Click a driver in the list → map flies to them and selects.
 * Click a marker on the map → selects the driver (highlights in list).
 */

const STATUS_COLORS = {
  on_ride: "#0ea5e9",
  available: "#16a34a",
  offline: "#94a3b8",
};

const DEFAULT_CENTER = [-95.358421, 29.749907]; // Houston
const REFRESH_MS = 15_000;

function bucketFor(d) {
  if (d.activeRideId) return "on_ride";
  if (d.workStatus === "On_Shift") return "available";
  return "offline";
}

function timeSince(ms) {
  if (!ms) return "—";
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Modern dispatch-style base map using CartoDB Voyager (free, no API key, hi-DPI).
// Falls back from the AWS Location Service style when Cognito auth is unavailable.
const FALLBACK_STYLE = {
  version: 8,
  sources: {
    cartodb: {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
        "https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution: "© <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors © <a href='https://carto.com/attributions'>CARTO</a>",
    },
  },
  layers: [{ id: "cartodb", type: "raster", source: "cartodb" }],
};

export const DispatchPage = () => {
  const navigate = useNavigate();
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map()); // driverId → maplibregl.Marker

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | on_ride | available | offline
  const [live, setLive] = useState(true);
  const [needsAttentionCount, setNeedsAttentionCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await getNeedsAttentionRides();
        setNeedsAttentionCount(unwrapList(res).length);
      } catch {
        // Non-fatal — pill just won't show
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30_000);
    return () => clearInterval(t);
  }, []);
  const [selectedId, setSelectedId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ---- data ----
  const load = useCallback(async () => {
    try {
      const res = await getLiveDrivers();
      const next = unwrapList(res);
      setDrivers(Array.isArray(next) ? next : []);
      setLastUpdated(Date.now());
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load live drivers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [live, load]);

  // ---- map init ----
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapDivRef.current,
      // Use modern CartoDB tiles directly — AWS Location Esri tiles look
      // dated and the Cognito auth path is unreliable in this env.
      style: FALLBACK_STYLE,
      center: DEFAULT_CENTER,
      zoom: 10,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = new Map();
    };
  }, []);

  // ---- sync markers ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const existing = markersRef.current;
    const seen = new Set();

    drivers.forEach((d) => {
      if (d.lat == null || d.lon == null) return;
      seen.add(d.driverDetailsId);
      const bg = STATUS_COLORS[bucketFor(d)] || "#475569";
      let marker = existing.get(d.driverDetailsId);
      if (!marker) {
        const el = document.createElement("div");
        el.style.cssText = `width:18px;height:18px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);cursor:pointer;background:${bg};`;
        el.onclick = () => setSelectedId(d.driverDetailsId);
        marker = new maplibregl.Marker({ element: el }).setLngLat([d.lon, d.lat]).addTo(map);
        existing.set(d.driverDetailsId, marker);
      } else {
        marker.setLngLat([d.lon, d.lat]);
        const el = marker.getElement();
        if (el) el.style.background = bg;
      }
    });

    // Remove stale
    Array.from(existing.keys()).forEach((id) => {
      if (!seen.has(id)) {
        existing.get(id).remove();
        existing.delete(id);
      }
    });
  }, [drivers]);

  // ---- fly to selected ----
  useEffect(() => {
    if (!selectedId || !mapRef.current) return;
    const d = drivers.find((x) => x.driverDetailsId === selectedId);
    if (d && d.lat != null && d.lon != null) {
      mapRef.current.flyTo({ center: [d.lon, d.lat], zoom: 13, duration: 600 });
    }
  }, [selectedId, drivers]);

  // ---- filtered list ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drivers
      .filter((d) => {
        if (filter !== "all" && bucketFor(d) !== filter) return false;
        if (!q) return true;
        return `${d.fullName || ""} ${d.phone || ""}`.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        // available → on_ride → offline, then name
        const order = { available: 0, on_ride: 1, offline: 2 };
        const ao = order[bucketFor(a)] ?? 99;
        const bo = order[bucketFor(b)] ?? 99;
        if (ao !== bo) return ao - bo;
        return (a.fullName || "").localeCompare(b.fullName || "");
      });
  }, [drivers, search, filter]);

  const counts = useMemo(() => {
    const c = { all: drivers.length, on_ride: 0, available: 0, offline: 0 };
    drivers.forEach((d) => (c[bucketFor(d)] = (c[bucketFor(d)] || 0) + 1));
    return c;
  }, [drivers]);

  const selected = drivers.find((d) => d.driverDetailsId === selectedId);

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)", backgroundColor: "#f8fafc" }}>
      {/* Left rail */}
      <Box
        sx={{
          width: 340,
          flexShrink: 0,
          borderRight: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Dispatch</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {needsAttentionCount > 0 ? (
                <Tooltip title={`${needsAttentionCount} ride${needsAttentionCount === 1 ? "" : "s"} need manual reassignment`}>
                  <Chip
                    size="small"
                    icon={<WarningAmberIcon style={{ color: "#fff" }} />}
                    label={needsAttentionCount}
                    onClick={() => navigate("/dispatch/needs-attention")}
                    sx={{
                      backgroundColor: "#dc2626",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#b91c1c" },
                    }}
                  />
                </Tooltip>
              ) : null}
              <FormControlLabel
                control={<Switch size="small" checked={live} onChange={(e) => setLive(e.target.checked)} />}
                label={<Typography variant="caption">Live</Typography>}
                sx={{ m: 0 }}
              />
              <Tooltip title="Refresh now">
                <IconButton size="small" onClick={load} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            size="small"
            placeholder="Search drivers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
            {[
              { key: "all", label: "All", color: "#475569" },
              { key: "available", label: "Available", color: STATUS_COLORS.available },
              { key: "on_ride", label: "On ride", color: STATUS_COLORS.on_ride },
              { key: "offline", label: "Offline", color: STATUS_COLORS.offline },
            ].map((b) => (
              <Chip
                key={b.key}
                size="small"
                label={`${b.label} ${counts[b.key] ?? 0}`}
                onClick={() => setFilter(b.key)}
                sx={{
                  height: 22,
                  fontSize: 11,
                  backgroundColor: filter === b.key ? b.color : "#f1f5f9",
                  color: filter === b.key ? "#ffffff" : "#475569",
                  fontWeight: filter === b.key ? 700 : 500,
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {error ? (
            <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
          {loading && drivers.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", p: 4 }}>
              No drivers match
            </Typography>
          ) : (
            filtered.map((d) => {
              const bucket = bucketFor(d);
              const isSel = d.driverDetailsId === selectedId;
              return (
                <Box
                  key={d.driverDetailsId}
                  onClick={() => setSelectedId(d.driverDetailsId)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: "1px solid #f1f5f9",
                    backgroundColor: isSel ? "#e0f2fe" : "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": { backgroundColor: isSel ? "#e0f2fe" : "#f8fafc" },
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: STATUS_COLORS[bucket],
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }} noWrap>
                      {d.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                      {d.phone || "—"}
                      {d.lkpTimeMs ? ` · seen ${timeSince(d.lkpTimeMs)}` : " · no position"}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {selected ? (
          <Box sx={{ borderTop: "1px solid #e2e8f0", p: 2, backgroundColor: "#f8fafc" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{selected.fullName}</Typography>
            <Typography variant="caption" display="block" color="text.secondary">{selected.phone || "—"}</Typography>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.5}>
              <Typography variant="caption">
                <strong>Status:</strong> {bucketFor(selected).replace("_", " ")}
                {selected.activeRideStatus ? ` (${selected.activeRideStatus})` : ""}
              </Typography>
              <Typography variant="caption">
                <strong>Last seen:</strong> {timeSince(selected.lkpTimeMs)}
              </Typography>
              {selected.lat != null ? (
                <Typography variant="caption">
                  <strong>Position:</strong> {selected.lat.toFixed(4)}, {selected.lon.toFixed(4)}
                </Typography>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                size="small"
                icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                label="Compliance"
                onClick={() => navigate(`/compliance/drivers/${selected.driverDetailsId}`)}
                sx={{ cursor: "pointer" }}
              />
            </Stack>
          </Box>
        ) : null}
      </Box>

      {/* Map */}
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box ref={mapDivRef} sx={{ position: "absolute", inset: 0 }} />
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            backgroundColor: "rgba(255,255,255,0.92)",
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: 11,
            color: "#475569",
            boxShadow: 1,
          }}
        >
          {drivers.length} driver{drivers.length === 1 ? "" : "s"}
          {lastUpdated ? ` · updated ${timeSince(lastUpdated)}` : ""}
        </Box>
      </Box>
    </Box>
  );
};

export default DispatchPage;
